import { axiosRubyClient as api } from '@/util/axios/axiosClient';

export interface TierBoundary {
  tier: string;
  label: string;
  min: number;
  max: number;
}
export interface PreferredPool {
  customer_id: number;
  customer_name: string | null;
  kind: 'favorited' | 'lta';
  standing: string | null;
}
export interface TrendStat {
  current: number | null;
  previous: number | null;
  trend: 'up' | 'down' | 'flat' | null;
}

export interface DependabilitySignal {
  key: string;
  label: string;
  percent: number | null;
  display_value: string;
  status: 'ok' | 'warn';
}

export interface DependabilityV2 {
  score: number;
  tier: string;
  tier_label: string;
  scoring_mode: 'activation' | 'reliability';
  computed_at: string;
  completed_shifts_count: number;
  // GRAVY-10404: DISPLAY-ONLY suspension signal. When suspended/permanently
  // removed, the tier badge shows "Suspended" instead of the tier. Optional so a
  // backend that predates the field still renders the tier normally.
  accountability_status?: 'active' | 'suspended' | 'permanently_removed';
  tier_boundaries: TierBoundary[];
  badges: {
    window_days: number;
    no_shows_count: number;
    // late ARRIVALS (check-ins past the grace window)
    late_count: number;
    // GRAVY-10402: all-time active late-drop (late cancellation) violations.
    // Optional so a backend that predates the field (rolling deploy) still
    // passes the isDependabilityV2 shape check; the UI defaults it to 0.
    late_cancels_count?: number;
    preferred_pools: PreferredPool[];
  };
  stats: {
    shifts_completed: number;
    on_time_rate: TrendStat;
    client_rating: TrendStat;
  };
  legacy: { tier: string; rating: number; label: string } | null;
  // optional so the UI tolerates a backend that predates the signals field
  signals?: DependabilitySignal[];
}

export interface DependabilityEvent {
  id: number;
  event_type: string;
  category: 'positive' | 'negative' | 'neutral';
  description: string;
  delta: number | null;
  score_after: number;
  occurred_at: string;
}

export interface DependabilityEventsResponse {
  success: boolean;
  data: DependabilityEvent[];
  pagination: {
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
}

// Runtime shape check for the dependability payload (fix round 3): during a
// rolling deploy (or any divergent backend revision) a success:true payload
// can arrive missing tier_boundaries / badges.preferred_pools /
// stats.on_time_rate — shapes that throw during render (TierPopover is
// mounted even while closed) and, with no error boundary in the app, blank
// the entire WorkerV2 route. Callers must fall back to the legacy card when
// this returns false. Only fields the components dereference unconditionally
// are checked; `signals` stays optional by design.
export const isDependabilityV2 = (data: unknown): data is DependabilityV2 => {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, any>;
  return (
    typeof d.score === 'number' &&
    typeof d.tier === 'string' &&
    Array.isArray(d.tier_boundaries) &&
    !!d.badges &&
    typeof d.badges === 'object' &&
    Array.isArray(d.badges.preferred_pools) &&
    typeof d.badges.window_days === 'number' &&
    typeof d.badges.no_shows_count === 'number' &&
    typeof d.badges.late_count === 'number' &&
    !!d.stats &&
    typeof d.stats === 'object' &&
    !!d.stats.on_time_rate &&
    !!d.stats.client_rating
  );
};

export const dependabilityV2API = {
  getDependability: async (
    workerId: number,
  ): Promise<{ success: boolean; data: DependabilityV2 }> =>
    (await api.get(`/api/admin/workers/${workerId}/dependability`)).data,

  getEvents: async (
    workerId: number,
    page = 1,
    perPage = 5,
  ): Promise<DependabilityEventsResponse> =>
    (
      await api.get(`/api/admin/workers/${workerId}/dependability/events`, {
        params: { page, per_page: perPage },
      })
    ).data,
};
